/**
 * Chip text for the /docs page: always »Posodobljeno <age> nazaj« (pure).
 *
 * Decision (Teo 2026-09-08): every entry, static ones included, wears ONE
 * green chip with the age of its last update — no status vocabulary, no
 * stale / override notes on the page. The anchor is the import
 * (`importedAt`), then the publisher's data date, then the served-since
 * moments; the age is measured in Europe/Ljubljana calendar days against the
 * response's `generatedAt` (never the browser clock) and worded in days,
 * months or years.
 */

import { ljubljanaDayDiff } from '../../shared/utils/ljubljanaTime.js';
import { docsCopy } from './docsCopy.js';

/** Whole Ljubljana days from `dateIso` to `generatedAt`, clamped at 0; null when unparseable. */
export function daysAgo(dateIso: string | null | undefined, generatedAt: string): number | null {
  if (!dateIso) return null;
  const n = ljubljanaDayDiff(new Date(dateIso), new Date(generatedAt));
  if (!Number.isFinite(n)) return null;
  return Math.max(0, n);
}

/** "danes" · "1 dan nazaj" · "12 dni nazaj" · "3 mesece nazaj" · "2 leti nazaj". */
export function agoText(days: number): string {
  if (days <= 0) return docsCopy.age.today;
  if (days < 30) return docsCopy.age.days(days);
  const months = Math.round(days / 30.4375);
  if (months < 12) return docsCopy.age.months(Math.max(1, months));
  return docsCopy.age.years(Math.max(1, Math.round(days / 365.25)));
}

/** The dates a chip may anchor on, in order of preference. */
export interface ChipDates {
  importedAt: string | null;
  vintage: string | null;
  desktop?: { servedAt: string | null };
  web?: { servedAt: string | null };
}

/** The import, else the publisher date, else when a product started serving it. */
export function chipDate(e: ChipDates): string | null {
  return e.importedAt ?? e.vintage ?? e.desktop?.servedAt ?? e.web?.servedAt ?? null;
}

/** »Posodobljeno <age> nazaj«, or the bare word when no date is known. */
export function chipText(dateIso: string | null, generatedAt: string): string {
  const n = daysAgo(dateIso, generatedAt);
  return n === null ? docsCopy.updated : docsCopy.updatedAgo(agoText(n));
}
